import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  DollarSign,
  Star,
  Clock,
  ToggleLeft,
  ToggleRight,
  LogOut,
  User,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfessionalDashboard({ user, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [dashboardRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/professional/dashboard`, config),
        axios.get(`${API}/professional/sessions`, config)
      ]);

      setDashboard(dashboardRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.patch(`${API}/professional/availability?available=${!available}`, {}, config);
      setAvailable(!available);
      toast.success(`You are now ${!available ? 'available' : 'unavailable'}`);
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  const upcomingSessions = dashboard?.upcoming_sessions || [];
  const completedSessions = sessions.filter((s) => s.status === 'completed');
  const scheduledSessions = sessions.filter((s) => s.status === 'scheduled');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#D32F2F]">Physiomobile</h1>
              <p className="text-sm text-slate-600">Professional Portal</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
                <p className="text-xs text-slate-500">{user.service_type}</p>
              </div>
              <button
                onClick={onLogout}
                data-testid="logout-btn"
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Mobile Navigation */}
        <div className="flex space-x-2 mb-6 md:hidden overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('home')}
            data-testid="tab-home-btn"
            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
              activeTab === 'home'
                ? 'bg-[#D32F2F] text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            data-testid="tab-sessions-btn"
            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
              activeTab === 'sessions'
                ? 'bg-[#D32F2F] text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            All Sessions
          </button>
        </div>

        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Availability Toggle */}
            <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Availability Status</h3>
                  <p className="text-sm text-slate-600">
                    {available ? 'You are currently accepting new sessions' : 'You are unavailable'}
                  </p>
                </div>
                <button
                  onClick={toggleAvailability}
                  data-testid="toggle-availability-btn"
                  className="flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all transform active:scale-95"
                  style={{
                    backgroundColor: available ? '#10B981' : '#94A3B8',
                    color: 'white'
                  }}
                >
                  {available ? (
                    <>
                      <ToggleRight className="w-5 h-5" />
                      <span>Available</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5" />
                      <span>Unavailable</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="stat-card" data-testid="earnings-today-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Earnings Today</p>
                <p className="text-3xl font-bold text-slate-900">${dashboard.earnings_today.toFixed(2)}</p>
              </div>

              <div className="stat-card" data-testid="earnings-month-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-sky-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Earnings This Month</p>
                <p className="text-3xl font-bold text-slate-900">${dashboard.earnings_month.toFixed(2)}</p>
              </div>

              <div className="stat-card" data-testid="rating-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Star className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Your Rating</p>
                <p className="text-3xl font-bold text-slate-900">{dashboard.rating.toFixed(1)}</p>
              </div>

              <div className="stat-card" data-testid="total-sessions-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Total Sessions</p>
                <p className="text-3xl font-bold text-slate-900">{dashboard.total_sessions}</p>
              </div>
            </div>

            {/* Upcoming Sessions */}
            <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900">Today's Sessions</h3>
                {dashboard.pending_requests > 0 && (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-amber-50 rounded-full">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-600">
                      {dashboard.pending_requests} Pending
                    </span>
                  </div>
                )}
              </div>

              {upcomingSessions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No upcoming sessions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      data-testid={`session-${session.id}-card`}
                      className="flex items-start space-x-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#D32F2F] flex items-center justify-center text-white font-bold flex-shrink-0">
                        {session.patient_name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{session.patient_name}</p>
                        <p className="text-sm text-slate-600">{session.service_type}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                          <span>{new Date(session.date).toLocaleTimeString()}</span>
                          <span className="capitalize">{session.location}</span>
                          <span className="font-semibold text-[#D32F2F]">${session.cost}</span>
                        </div>
                      </div>
                      <span className={`badge-status badge-${session.status} flex-shrink-0`}>
                        {session.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="space-y-6">
            {/* Sessions Tabs */}
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="border-b border-slate-200 p-4">
                <h3 className="text-lg font-bold text-slate-900">All Sessions</h3>
              </div>

              <div className="p-6 space-y-4">
                <h4 className="font-semibold text-slate-900">Scheduled ({scheduledSessions.length})</h4>
                {scheduledSessions.length === 0 ? (
                  <p className="text-sm text-slate-500">No scheduled sessions</p>
                ) : (
                  <div className="space-y-3">
                    {scheduledSessions.map((session) => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{session.patient_name}</p>
                          <p className="text-sm text-slate-600">
                            {new Date(session.date).toLocaleDateString()} -{' '}
                            {new Date(session.date).toLocaleTimeString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[#D32F2F]">${session.cost}</p>
                          <p className="text-xs text-slate-500 capitalize">{session.location}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <h4 className="font-semibold text-slate-900 mt-6">Completed ({completedSessions.length})</h4>
                {completedSessions.length === 0 ? (
                  <p className="text-sm text-slate-500">No completed sessions</p>
                ) : (
                  <div className="space-y-3">
                    {completedSessions.map((session) => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{session.patient_name}</p>
                          <p className="text-sm text-slate-600">
                            {new Date(session.date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-emerald-600">${session.cost}</p>
                          {session.rating && (
                            <p className="text-xs text-amber-600">{session.rating} ★</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 md:hidden z-30">
        <div className="flex justify-around">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center space-y-1 ${
              activeTab === 'home' ? 'text-[#D32F2F]' : 'text-slate-400'
            }`}
          >
            <User className="w-6 h-6" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`flex flex-col items-center space-y-1 ${
              activeTab === 'sessions' ? 'text-[#D32F2F]' : 'text-slate-400'
            }`}
          >
            <Calendar className="w-6 h-6" />
            <span className="text-xs font-medium">Sessions</span>
          </button>
        </div>
      </div>
    </div>
  );
}
